import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import type { OneAccountView } from '@/api/map/oneAccount'
import type { PlStatementRowView } from '@/api/map/plStatement'
import type {
  SurveillanceCurrency,
  SurveillanceGraphFilter,
} from '@/features/surveillance/types'

/** Lab sportsbook-detail View Graph hides these betCategory values. */
const HIDDEN_BET_CATEGORIES = new Set([
  'Line Session',
  'Bookmaker',
  'Custom Session',
])

/**
 * Lab `sportsbook-detail` View Graph gate:
 * Company Master + SportsBook + not Line Session / Bookmaker / Custom Session.
 * Fancy / casino / card never show the button.
 */
export function canShowPlViewGraph(detail: Pick<
  OneAccountView,
  'gameName' | 'betCategory' | 'isCardGame' | 'isLiveCasino' | 'isFundist'
>): boolean {
  if (detail.isCardGame || detail.isLiveCasino || detail.isFundist) return false
  if (detail.gameName !== 'SportsBook') return false
  return !HIDDEN_BET_CATEGORIES.has(detail.betCategory.trim())
}

/**
 * Settlement often stamps `event_id = event_name` when the vendor id is blank.
 * BQ `bqGetBetsForSurv` filters on vendor_event_id — a display name yields empty `h`.
 */
export function looksLikeEventNameNotId(
  eventId: string,
  eventName?: string,
): boolean {
  const id = eventId.trim()
  if (!id) return true
  if (eventName && id.toLowerCase() === eventName.trim().toLowerCase()) return true
  if (/\s/.test(id)) return true
  // Catalog UUID mistakenly stored as event_id (Lab surveillance-events-sql skips these).
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    return true
  }
  return false
}

/** Archive surveillance filter from Game Detail (Lab getGraph on accountStatement[0]). */
export function plArchiveGraphFilter(
  detail: OneAccountView,
  row: PlStatementRowView,
): SurveillanceGraphFilter | null {
  const eventType = (detail.eventType || detail.subGame || row.sport).trim()
  const eventId = (detail.eventId || row.eventId).trim()
  const marketId = (detail.marketId || row.marketId).trim()
  if (!eventType || !eventId || !marketId) return null

  const currencyRaw = (detail.currencyType ?? row.currencyType ?? 'All').trim()
  const currencyType = (
    currencyRaw === 'INR' ||
    currencyRaw === 'POINTS' ||
    currencyRaw === 'POINT@1' ||
    currencyRaw === 'All'
      ? currencyRaw
      : 'All'
  ) as SurveillanceCurrency

  return {
    mode: 'archive',
    eventType,
    eventId,
    marketId,
    playerNames: detail.username.trim() ? [detail.username.trim()] : [],
    fromStake: null,
    toStake: null,
    currencyType,
  }
}

/**
 * Resolve a vendor/Betfair event id when Game Detail only has the event name.
 * Prefer a catalog hit whose markets include this settlement marketId.
 */
export async function resolvePlGraphEventId(opts: {
  eventId: string
  eventName: string
  marketId: string
}): Promise<string> {
  const raw = opts.eventId.trim()
  if (!looksLikeEventNameNotId(raw, opts.eventName)) return raw

  const q = (opts.eventName || raw).trim()
  if (!q) return raw

  try {
    const res = await cmApi.eventSearch(authHeaders(), q, 25)
    const hits = res.events ?? []
    const marketId = opts.marketId.trim()

    for (const hit of hits) {
      const vendorEventId = hit.vendorEventId?.trim()
      if (!vendorEventId) continue
      if (!marketId) {
        if (hit.name.trim().toLowerCase() === q.toLowerCase()) return vendorEventId
        continue
      }
      try {
        const markets = await cmApi.browseMarkets(authHeaders(), vendorEventId)
        if (
          markets.markets.some(
            (m) => m.vendorMarketId === marketId || m.id === marketId,
          )
        ) {
          return vendorEventId
        }
      } catch {
        // try next hit
      }
    }

    const exact = hits.find((h) => h.name.trim().toLowerCase() === q.toLowerCase())
    if (exact?.vendorEventId?.trim()) return exact.vendorEventId.trim()
    if (hits[0]?.vendorEventId?.trim()) return hits[0].vendorEventId.trim()
  } catch {
    // keep settlement stamp
  }
  return raw
}
